import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import type { ExecutionOutput } from '@/hooks/useRealtimeWorkflows';
import type { CopySightResult } from '@/types/copysight';

const outputKey = (o: Pick<ExecutionOutput, 'filename' | 'subfolder'>): string => `${o.subfolder || ''}/${o.filename}`;

interface UseExecutionCopySightReturn {
  // CopySight results with detections, keyed by output
  reports: Record<string, CopySightResult>;
  loading: boolean;
}

// Fetches an execution once it has completed with `?include=copysight`: the
// backend adds the full CopySight report (detections + boxes) to each inspected
// output. The same request re-signs expired output URLs, as the old refresh did.
// `refreshKey` should change when an output is (re)inspected, to fetch again.
export const useExecutionCopySight = (
  promptId: string | undefined,
  enabled: boolean,
  refreshKey?: string,
): UseExecutionCopySightReturn => {
  const [reports, setReports] = useState<Record<string, CopySightResult>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setReports({});
    if (!promptId || !enabled) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    apiFetch(`/executions/${promptId}?include=copysight`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const data: { outputs?: ExecutionOutput[] } = await res.json();
        if (cancelled) return;
        const next: Record<string, CopySightResult> = {};
        for (const output of data.outputs ?? []) {
          if (output.copysight) next[outputKey(output)] = output.copysight;
        }
        setReports(next);
      })
      .catch(() => {
        // Non-fatal: outputs keep the stored summary (status badge, no boxes)
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [promptId, enabled, refreshKey]);

  return { reports, loading };
};

// Prefer the enriched result (has detections) over the stored summary.
export const mergeCopySight = (outputs: ExecutionOutput[], reports: Record<string, CopySightResult>): ExecutionOutput[] =>
  outputs.map((output) => {
    const report = reports[outputKey(output)];
    return report ? { ...output, copysight: report } : output;
  });

// Changes when any output is (re)inspected, so the hook refetches.
export const copysightRefreshKey = (outputs: ExecutionOutput[] | undefined): string =>
  (outputs ?? []).map((o) => o.copysight?.inspectedAt ?? '').join('|');
